import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useEditions } from "@/context/EditionsContext";
import {
  SIZES, COLORS, eur, shippingFor, FREE_SHIPPING,
} from "@/lib/shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sizeLabel = (id) => SIZES.find((s) => s.id === id)?.label || id;
const colorLabel = (id) => COLORS.find((c) => c.id === id)?.label || id;

export default function CartDrawer() {
  const {
    items, removeItem, setQuantity, total, lineKey,
    isOpen, closeCart, shipZone, setShipZone,
  } = useCart();
  const { t, lang } = useLanguage();
  const { liveRemaining, liveEditionTotal } = useEditions();
  const [loading, setLoading] = useState(false);

  const shipping = shippingFor(items, shipZone);
  const progress = Math.min(total / FREE_SHIPPING, 1);

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        ship_zone: shipZone,
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          color: i.color,
          size: i.size,
          framing: i.framing || "unframed",
          frame_color: i.frame_color || null,
          tier: i.tier || null,
          custom_word: i.custom_word || null,
          bundle_items: i.bundle_items || null,
          quantity: i.quantity,
        })),
      };
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error(t("cart.error"));
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => (o ? null : closeCart())}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-[#faf8f5] border-l border-[#111111]/12 p-0 flex flex-col"
      >
        <SheetHeader className="px-7 pt-7 pb-5 border-b border-[#111111]/10">
          <SheetTitle className="font-display text-2xl tracking-tight text-[#111111] font-normal">
            {t("cart.title")}
            {items.length > 0 && (
              <span className="ml-2 text-sm font-light text-[#a89f8e]">
                ({items.reduce((n, i) => n + i.quantity, 0)})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-start justify-center px-7" data-testid="cart-empty">
            <p className="font-light text-[#111111]/70">{t("cart.empty")}</p>
            <Link
              to="/shop"
              onClick={closeCart}
              data-testid="cart-discover-link"
              className="mt-6 inline-flex items-center gap-2 border border-[#111111] px-6 py-3 text-sm font-light hover:bg-[#111111] hover:text-[#faf8f5] transition-colors duration-500"
            >
              {t("cart.discover")}
            </Link>
          </div>
        ) : (
          <>
            {/* Free-shipping progress */}
            <div className="px-7 pt-5" data-testid="free-shipping-bar">
              <p className="text-xs font-light text-[#111111]/70">
                {shipping === 0 ? (
                  <span className="text-[#111111]">{t("cart.freeUnlocked")}</span>
                ) : (
                  <>
                    <span className="font-display italic">{eur(FREE_SHIPPING - total, lang)}</span>{" "}
                    {t("cart.freeRemaining")}
                  </>
                )}
              </p>
              <div className="mt-2 h-px bg-[#111111]/12">
                <div
                  className="h-px bg-[#111111] transition-all duration-700"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-5">
              {items.map((i) => {
                const k = lineKey(i);
                const framingLabel =
                  i.framing === "framed"
                    ? `${t("product.framed")} · ${i.frame_color === "black" ? t("product.blackOak") : t("product.oak")}`
                    : t("product.unframed");
                const ed = liveEditionTotal(i.size, i.color, i.tier);
                const rem = liveRemaining(i.product_id, i.size, i.color, i.tier);
                return (
                  <div
                    key={k}
                    data-testid={`cart-item-${k}`}
                    className="flex items-start gap-4 border-b border-[#111111]/10 py-5 last:border-b-0"
                  >
                    <div className="h-20 w-16 shrink-0 border gallery-line bg-white overflow-hidden">
                      <img src={i.image} alt={i.product_name} className="h-full w-full object-contain p-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display italic text-base text-[#111111]">
                        {i.product_name}
                      </h3>
                      {i.bundle_items ? (
                        <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#a89f8e] leading-relaxed">
                          {i.bundle_items
                            .map((b) => `${b.product_name.replace("The ", "")} (${b.color === "noir" ? "Noir" : "Bi-color"})`)
                            .join(" · ")}
                        </p>
                      ) : i.custom_word ? (
                        <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#a89f8e] leading-relaxed">
                          {t("mto.unique")}
                          <br />
                          {colorLabel(i.color)} — {sizeLabel(i.size)}
                          <br />
                          {framingLabel}
                        </p>
                      ) : (
                        <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#a89f8e] leading-relaxed">
                          {colorLabel(i.color)} — {sizeLabel(i.size)}
                          <br />
                          {i.tier === "collector" ? t("collector.label") : framingLabel}
                        </p>
                      )}
                      {ed > 0 && !i.custom_word && !i.bundle_items && i.tier !== "collector" && (
                        <p className="mt-1 text-[11px] font-light text-[#111111]/45">
                          {t("cart.editionOf")} {ed} — {rem} {t("cart.remaining")}
                        </p>
                      )}
                      <div className="mt-2 inline-flex items-center border border-[#111111]/20">
                        <button
                          onClick={() => setQuantity(k, i.quantity - 1)}
                          className="grid h-7 w-7 place-items-center hover:bg-[#f1ede7]"
                          aria-label="minus"
                        >
                          <Minus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                        <span className="w-8 text-center text-sm">{i.quantity}</span>
                        <button
                          onClick={() => setQuantity(k, i.quantity + 1)}
                          className="grid h-7 w-7 place-items-center hover:bg-[#f1ede7]"
                          aria-label="plus"
                        >
                          <Plus className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-light text-sm text-[#111111]">
                        {eur(i.unit_price * i.quantity, lang)}
                      </p>
                      <button
                        onClick={() => removeItem(k)}
                        data-testid={`remove-${k}`}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-light text-[#111111]/50 hover:text-[#111111]"
                      >
                        <X className="h-3 w-3" strokeWidth={1.5} /> {t("cart.remove")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-[#111111]/10 px-7 py-6">
              {/* Shipping zone */}
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-[#111111]/50">
                  {t("cart.shippingZone")}
                </span>
                <div className="flex gap-2">
                  {["FR", "EU"].map((z) => (
                    <button
                      key={z}
                      onClick={() => setShipZone(z)}
                      data-testid={`zone-${z}`}
                      className={`border px-4 py-1.5 text-xs font-light transition-colors duration-300 ${
                        shipZone === z
                          ? "border-[#111111] bg-[#111111] text-[#faf8f5]"
                          : "border-[#111111]/25 text-[#111111]/70 hover:border-[#111111]"
                      }`}
                    >
                      {z === "FR" ? t("cart.zoneFR") : t("cart.zoneEU")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="font-light text-[#111111]/60">{t("cart.subtotal")}</span>
                  <span className="font-light text-[#111111]" data-testid="cart-subtotal">
                    {eur(total, lang)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-light text-[#111111]/60">{t("cart.shipping")}</span>
                  <span className="font-light text-[#111111]" data-testid="cart-shipping">
                    {shipping === 0 ? t("cart.free") : eur(shipping, lang)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#111111]/10">
                  <span className="font-light text-[#111111]/70">{t("cart.total")}</span>
                  <span className="font-display text-lg text-[#111111]" data-testid="cart-total">
                    {eur(total + shipping, lang)}
                  </span>
                </div>
              </div>

              <button
                onClick={checkout}
                disabled={loading}
                data-testid="checkout-button"
                className="mt-5 flex w-full items-center justify-center gap-2 bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85 disabled:opacity-50"
              >
                {loading ? t("cart.checkoutLoading") : t("cart.checkout")}
                {!loading && <ArrowRight className="h-4 w-4" strokeWidth={1.5} />}
              </button>
              <p className="mt-3 text-center text-[11px] font-light text-[#111111]/45">
                {t("cart.guest")}
              </p>
              <p className="mt-1.5 text-center text-[11px] font-light text-[#111111]/45">
                {t("fulfil.line")}
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
